import { useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useAppSelector } from '@/store/hooks'
import { useGetLessonBlocksQuery } from '@/features/editor/lessonsApi'
import { compareByOrder } from '@/features/editor/lib/orderUtils'
import { badgeItems } from '@/features/editor/lib/designElements'
import type { Block } from '@/types/blocks'

export type EditScope = 'block' | 'lesson' | 'selection'

const hasCustomIcon = (blocks: Block[]): boolean =>
  blocks.some(
    (block) =>
      block.type === 'design' &&
      block.elements.some(
        (el) => el.type === 'badge' && badgeItems(el.badges).some((b) => b.iconType === 'custom'),
      ),
  )

/**
 * Область правки: выбранный блок, весь урок либо произвольный мультивыбор
 * (п.10 бэк-лога заказчика, Ctrl/Cmd-клик по миниатюре — `BlockThumbnail`).
 * Набор кликов копится в `editorSlice.selectedBlockIds`; этот хук —
 * единственный, кто строит по нему ОБЛАСТЬ ПРАВКИ. Второй читатель набора,
 * `useCourseTimeline`, только пробрасывает отметку в миниатюру и логики
 * области не дублирует. (`selectedBlockId`-якорь панели/превью/черновика
 * трогать нельзя, это отдельное поле с многими читателями.)
 *
 * Блоки урока уже лежат в кеше (их держит лента блоков) — отдельного обхода
 * курса тут нет и быть не должно.
 */
export const useEditScope = () => {
  const [scope, setScope] = useState<EditScope>('block')
  const selectedLessonId = useAppSelector((s) => s.editor.selectedLessonId)
  const selectedBlockId = useAppSelector((s) => s.editor.selectedBlockId)
  const selectedBlockIds = useAppSelector((s) => s.editor.selectedBlockIds) ?? []
  // `isLoading`, не `isFetching`: нас интересует «данных для ЭТОГО lessonId
  // ещё нет вовсе» (первый ответ впереди — утверждать «блоков нет» нельзя),
  // а не «идёт фоновый рефетч уже известных блоков», где `isFetching` истинен
  // даже когда `blocks` уже актуальны и скрывать их незачем.
  const { data: blocks, isLoading: isLoadingBlocks } = useGetLessonBlocksQuery(
    selectedLessonId ?? skipToken,
  )
  const resolvedBlocks = blocks ?? []

  // Узкий тип без каста: null проверяется тут же, внутри ветки его использования.
  const hasBlock = selectedBlockId !== null && resolvedBlocks.some((b) => b.id === selectedBlockId)
  const hasLessonBlocks = resolvedBlocks.length > 0

  // Порядок списка = порядок правок и событий в ленте (§6.1), поэтому и урок,
  // и мультивыбор отдаём в порядке `order`, а не в порядке прихода из кеша
  // или порядке кликов. Id за пределами блоков этого урока (стек кликов
  // мог накопиться в другом уроке) отфильтрованы — актуален только он.
  const lessonIds = [...resolvedBlocks].sort(compareByOrder).map((b) => b.id)
  const selectionIds = resolvedBlocks
    .filter((b) => selectedBlockIds.includes(b.id))
    .sort(compareByOrder)
    .map((b) => b.id)
  const hasSelection = selectionIds.length > 0

  let blockIds: number[]
  switch (scope) {
    case 'block':
      blockIds = selectedBlockId !== null && hasBlock ? [selectedBlockId] : []
      break
    case 'lesson':
      blockIds = lessonIds
      break
    case 'selection':
      blockIds = selectionIds
      break
    default: {
      // Новый вариант EditScope обязан сломать сборку здесь, а не молча
      // остаться без блоков.
      const _unhandled: never = scope
      blockIds = _unhandled
    }
  }

  // Контракт §6.3: у бейджа с `iconType: 'custom'` `iconValue` — ссылка на
  // файл, щитом сервера не закрыта, и уцелеет, только если модель `badges`
  // вовсе не тронет. Автор обязан узнать об этом ДО списания (§11.2).
  const scopedBlocks = resolvedBlocks.filter((b) => blockIds.includes(b.id))
  const hasCustomBadgeIcon = hasCustomIcon(scopedBlocks)

  return {
    scope,
    setScope,
    blockIds,
    count: blockIds.length,
    hasBlock,
    hasLessonBlocks,
    hasSelection,
    // Размер НАБОРА мультивыбора (пересечение selectedBlockIds с блоками
    // этого урока) — в отличие от `count`, не зависит от активной области:
    // нужен вкладке «Выбранные» для бейджа-счётчика (п.10), который обязан
    // быть виден ДО переключения области, а не только после него.
    selectionCount: selectionIds.length,
    hasCustomBadgeIcon,
    isLoadingBlocks,
  }
}
